import { CampanhaSimulada, SugestaoEvolutiva } from '../../types/simulador';

interface IAInsightsProps {
  campanha: CampanhaSimulada;
}

export const IAInsights = ({ campanha }: IAInsightsProps) => {
  const getImpactoColor = (impacto: 'baixo' | 'medio' | 'alto') => {
    switch (impacto) {
      case 'alto': return 'border-red-300 bg-red-50';
      case 'medio': return 'border-orange-300 bg-orange-50';
      case 'baixo': return 'border-blue-300 bg-blue-50';
    }
  };

  const getImpactoIcon = (impacto: 'baixo' | 'medio' | 'alto') => {
    switch (impacto) {
      case 'alto': return 'ri-error-warning-line text-red-600';
      case 'medio': return 'ri-alert-line text-orange-600';
      case 'baixo': return 'ri-information-line text-blue-600';
    }
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'otimizacao': return 'ri-lightbulb-line text-yellow-600';
      case 'simplificacao': return 'ri-scissors-line text-blue-600';
      case 'melhoria': return 'ri-arrow-up-line text-green-600';
      case 'alerta': return 'ri-alarm-warning-line text-red-600';
      default: return 'ri-information-line text-gray-600';
    }
  };

  return (
    <div className="space-y-6">
      {/* Mensagem de Compliance */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start gap-3">
          <i className="ri-shield-check-line text-2xl text-blue-600 flex-shrink-0"></i>
          <div>
            <h4 className="font-semibold text-blue-900 mb-1">Simulação com IA e Compliance</h4>
            <p className="text-sm text-blue-800">
              Esta simulação utiliza inteligência artificial, campos abstratos de Peso Comercial e Perfil Econômico 
              e recompensas reais do catálogo interno, sem uso de dados financeiros sensíveis.
            </p>
          </div>
        </div>
      </div>

      {/* Sugestões Evolutivas */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-brain-line text-orange-600"></i>
          Sugestões da IA ({campanha.sugestoesEvolutivas.length})
        </h3>

        {campanha.sugestoesEvolutivas.length === 0 ? (
          <div className="text-center py-8">
            <i className="ri-checkbox-circle-line text-5xl text-green-500 mb-3"></i>
            <p className="text-gray-600 font-medium">Campanha bem estruturada!</p>
            <p className="text-sm text-gray-500 mt-1">Não há sugestões de melhoria no momento.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {campanha.sugestoesEvolutivas.map((sugestao) => (
              <div
                key={sugestao.id}
                className={`border-l-4 rounded-lg p-4 ${getImpactoColor(sugestao.impacto)}`}
              >
                <div className="flex items-start gap-3">
                  <i className={`${getTipoIcon(sugestao.tipo)} text-2xl flex-shrink-0 mt-1`}></i>
                  <div className="flex-1">
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-semibold text-gray-900">{sugestao.titulo}</h4>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        sugestao.impacto === 'alto' ? 'bg-red-100 text-red-700' :
                        sugestao.impacto === 'medio' ? 'bg-orange-100 text-orange-700' :
                        'bg-blue-100 text-blue-700'
                      }`}>
                        Impacto {sugestao.impacto}
                      </span>
                    </div>
                    <p className="text-sm text-gray-700 mb-2">{sugestao.descricao}</p>
                    <p className="text-xs text-gray-500 flex items-center gap-1">
                      <i className="ri-information-line"></i>
                      Baseado em: {sugestao.baseadoEm}
                    </p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Análise Comparativa */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-line-chart-line text-orange-600"></i>
          Análise Comparativa
        </h3>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-lg border border-green-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-green-500 rounded-full flex items-center justify-center text-white">
                <i className="ri-thumb-up-line"></i>
              </div>
              <h4 className="font-semibold text-gray-900">Pontos Fortes</h4>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-green-600 mt-0.5"></i>
                <span>Distribuição equilibrada de recompensas entre níveis</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-green-600 mt-0.5"></i>
                <span>Metas progressivas bem definidas</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-check-line text-green-600 mt-0.5"></i>
                <span>Uso adequado da Matriz Peso × Perfil</span>
              </li>
            </ul>
          </div>

          <div className="p-4 bg-gradient-to-br from-blue-50 to-indigo-50 rounded-lg border border-blue-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 bg-blue-500 rounded-full flex items-center justify-center text-white">
                <i className="ri-lightbulb-line"></i>
              </div>
              <h4 className="font-semibold text-gray-900">Oportunidades</h4>
            </div>
            <ul className="space-y-2 text-sm text-gray-700">
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-line text-blue-600 mt-0.5"></i>
                <span>Adicionar mais missões diárias para engajamento contínuo</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-line text-blue-600 mt-0.5"></i>
                <span>Criar distintivos específicos para produtos estratégicos</span>
              </li>
              <li className="flex items-start gap-2">
                <i className="ri-arrow-right-line text-blue-600 mt-0.5"></i>
                <span>Considerar recompensas de experiência nos níveis iniciais</span>
              </li>
            </ul>
          </div>
        </div>
      </div>

      {/* Métricas de Qualidade */}
      <div className="bg-white rounded-lg shadow-sm p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-4 flex items-center gap-2">
          <i className="ri-dashboard-3-line text-orange-600"></i>
          Métricas de Qualidade da Campanha
        </h3>
        
        <div className="space-y-4">
          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Engajamento Estimado</span>
              <span className="text-sm font-semibold text-green-600">Alto</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-gradient-to-r from-green-500 to-green-600 h-2 rounded-full" style={{ width: '85%' }}></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">85/100 - Boa variedade de metas e recompensas</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Clareza das Regras</span>
              <span className="text-sm font-semibold text-blue-600">Muito Boa</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-gradient-to-r from-blue-500 to-blue-600 h-2 rounded-full" style={{ width: '90%' }}></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">90/100 - Metas e objetivos bem definidos</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Atratividade das Recompensas</span>
              <span className="text-sm font-semibold text-orange-600">Boa</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-gradient-to-r from-orange-500 to-orange-600 h-2 rounded-full" style={{ width: '78%' }}></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">78/100 - Mix equilibrado de tipos de recompensas</p>
          </div>

          <div>
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-gray-700">Viabilidade Operacional</span>
              <span className="text-sm font-semibold text-purple-600">Excelente</span>
            </div>
            <div className="w-full bg-gray-200 rounded-full h-2">
              <div className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full" style={{ width: '92%' }}></div>
            </div>
            <p className="text-xs text-gray-500 mt-1">92/100 - Complexidade adequada para gestão</p>
          </div>
        </div>
      </div>

      {/* Próximos Passos */}
      <div className="bg-gradient-to-br from-orange-50 to-yellow-50 border border-orange-200 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-gray-800 mb-3 flex items-center gap-2">
          <i className="ri-roadmap-line text-orange-600"></i>
          Próximos Passos Recomendados
        </h3>
        <ol className="space-y-2 text-sm text-gray-700">
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">1</span>
            <span>Revisar e ajustar as metas conforme necessário</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">2</span>
            <span>Validar as recompensas selecionadas com o catálogo interno</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">3</span>
            <span>Aplicar as sugestões de melhoria da IA</span>
          </li>
          <li className="flex items-start gap-3">
            <span className="flex-shrink-0 w-6 h-6 bg-orange-500 text-white rounded-full flex items-center justify-center text-xs font-bold">4</span>
            <span>Criar a campanha oficial no sistema</span>
          </li>
        </ol>
      </div>
    </div>
  );
};
